import React, { useState, useEffect, useRef } from "react";
import { getInterestList } from "./usePromoteListingApi";

const GENDERS = ["All", "Male", "Female", "Non-binary"];
// Matches the backend's real allowed values (configuration/ad-targeting.js) - "Mobile"/"Desktop"
// aren't real distinct values there (targeting works by OS), and "Web" wasn't offered before.
const DEVICES = ["All", "Android", "IOS", "Web"];
// Mirrors configuration/ad-targeting.js's AD_TARGETING_MIN_AGE/AD_TARGETING_MAX_AGE defaults
// (18/65) - the server rejects anything outside this range, so keep in sync if those env vars
// ever change. There's no API to fetch this range dynamically today.
const AGE_MIN = 18;
const AGE_MAX = 65;

const formatCategoryLabel = (category) =>
  category
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const PillGroup = ({ options, value, onChange }) => (
  <div className="flex gap-3 flex-wrap">
    {options.map((opt) => {
      const selected = value.toLowerCase() === opt.toLowerCase();
      return (
        <button
          key={opt}
          type="button"
          onClick={() => onChange(opt.toLowerCase())}
          className={`px-5 py-2 rounded-full text-sm font-semibold ${
            selected ? "bg-[#095228] text-white" : "border border-[#c3c5d9] text-[#434656]"
          }`}
        >
          {opt}
        </button>
      );
    })}
  </div>
);

const Toggle = ({ checked, onChange }) => (
  <button
    type="button"
    onClick={() => onChange(!checked)}
    className={`w-14 h-7 rounded-full relative shrink-0 overflow-hidden transition-colors focus:outline-none ${
      checked ? "bg-[#095228]" : "bg-[#c3c5d9]"
    }`}
  >
    <span
      className={`absolute top-1 w-5 h-5 rounded-full bg-white shadow-md transition-all ${
        checked ? "left-8" : "left-1"
      }`}
    />
  </button>
);

const CheckBox = ({ checked }) => (
  <span
    className={`shrink-0 w-6 h-6 rounded-md flex items-center justify-center ${
      checked ? "bg-[#095228]" : "border-2 border-[#c3c5d9]"
    }`}
  >
    {checked && (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
        <path d="M5 13l4 4L19 7" />
      </svg>
    )}
  </span>
);

const PLACEMENTS = [
  { key: "searchResult", label: "Search Result", badge: "Most Popular", description: "Your Product can appear when buyers Search for products related to your listing." },
  { key: "marketplaceFeed", label: "Marketplace Feed", description: "Your Product may appear in relevant market place" },
  { key: "categoryResults", label: "Category Results", description: "Display your product while buyers browse relevant product" },
];

export default function PromoteTargeting({ formData, updateFormData, onNext, onBack }) {
  const [availableInterests, setAvailableInterests] = useState([]);
  const [interestsError, setInterestsError] = useState(null);
  const [interestDropdownOpen, setInterestDropdownOpen] = useState(false);
  const interestDropdownRef = useRef(null);
  const canContinue = formData.adSetName.trim();

  // interest_ids on the backend must be real, existing Interest row ids (validated) - not
  // free text, so this fetches the real list to select from instead of letting the seller type.
  useEffect(() => {
    getInterestList()
      .then(setAvailableInterests)
      .catch(() => setInterestsError("Couldn't load interests. Try again later."));
  }, []);

  // Close the dropdown on an outside click, same as any standard multiselect.
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (interestDropdownRef.current && !interestDropdownRef.current.contains(e.target)) {
        setInterestDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleInterest = (interest) => {
    const isSelected = formData.interests.some((i) => i.id === interest.id);
    updateFormData({
      interests: isSelected
        ? formData.interests.filter((i) => i.id !== interest.id)
        : [...formData.interests, { id: interest.id, label: formatCategoryLabel(interest.category) }],
    });
  };

  const togglePlacement = (key) => {
    updateFormData({ placements: { ...formData.placements, [key]: !formData.placements[key] } });
  };

  return (
    <div>
      <label className="block text-sm font-semibold text-black mb-1">
        Ad Set Name<span className="text-red-500">*</span>
      </label>
      <input
        type="text"
        value={formData.adSetName}
        onChange={(e) => updateFormData({ adSetName: e.target.value })}
        placeholder="Enter your Ad Set Name"
        className="w-full border border-[#c3c5d9] rounded-xl px-4 py-3 mb-6 text-sm"
      />

      <label className="block text-sm font-semibold text-black mb-2">Gender</label>
      <div className="mb-6">
        <PillGroup options={GENDERS} value={formData.gender} onChange={(v) => updateFormData({ gender: v })} />
      </div>

      <label className="block text-sm font-semibold text-black mb-2">Audience Age</label>
      <div className="relative h-8 mb-2">
        <div className="absolute top-3 left-0 right-0 h-1 bg-[#d9d9d9] rounded-full" />
        <div
          className="absolute top-3 h-1 bg-[#095228] rounded-full"
          style={{
            left: `${((formData.minAge - AGE_MIN) / (AGE_MAX - AGE_MIN)) * 100}%`,
            right: `${100 - ((formData.maxAge - AGE_MIN) / (AGE_MAX - AGE_MIN)) * 100}%`,
          }}
        />
        <input
          type="range"
          min={AGE_MIN}
          max={AGE_MAX}
          value={formData.minAge}
          onChange={(e) => updateFormData({ minAge: Math.min(Number(e.target.value), formData.maxAge - 1) })}
          className="absolute w-full appearance-none bg-transparent pointer-events-none [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-[#095228] [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-white [&::-webkit-slider-thumb]:shadow"
        />
        <input
          type="range"
          min={AGE_MIN}
          max={AGE_MAX}
          value={formData.maxAge}
          onChange={(e) => updateFormData({ maxAge: Math.max(Number(e.target.value), formData.minAge + 1) })}
          className="absolute w-full appearance-none bg-transparent pointer-events-none [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-[#095228] [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-white [&::-webkit-slider-thumb]:shadow"
        />
      </div>
      <div className="flex justify-between text-xs text-[#747474] mb-6">
        <span>{AGE_MIN}</span>
        <span>{formData.minAge} - {formData.maxAge}</span>
        <span>{AGE_MAX}</span>
      </div>

      <label className="block text-sm font-semibold text-black mb-2">Interests</label>
      {interestsError && <p className="text-red-500 text-xs mb-2">{interestsError}</p>}
      <div className="relative mb-3" ref={interestDropdownRef}>
        <button
          type="button"
          onClick={() => setInterestDropdownOpen((open) => !open)}
          className="w-full text-left border border-[#c3c5d9] rounded-xl px-4 py-3 text-sm text-[#9a9a9a] flex items-center justify-between"
        >
          Add Interests
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9a9a9a" strokeWidth="2.5" style={{ transform: interestDropdownOpen ? "rotate(180deg)" : "none" }}>
            <path d="M6 9l6 6 6-6" />
          </svg>
        </button>

        {interestDropdownOpen && (
          <div className="absolute z-10 top-full mt-1 w-full bg-white border border-[#c3c5d9] rounded-xl shadow-lg max-h-56 overflow-y-auto">
            {availableInterests.map((interest) => {
              const selected = formData.interests.some((i) => i.id === interest.id);
              return (
                <button
                  key={interest.id}
                  type="button"
                  onClick={() => toggleInterest(interest)}
                  className="w-full flex items-center justify-between px-4 py-2.5 text-sm text-left hover:bg-gray-50"
                >
                  {formatCategoryLabel(interest.category)}
                  {selected && (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#095228" strokeWidth="3"><path d="M5 13l4 4L19 7" /></svg>
                  )}
                </button>
              );
            })}
            {!availableInterests.length && (
              <p className="px-4 py-2.5 text-xs text-[#9a9a9a]">
                {interestsError || "No interests available yet."}
              </p>
            )}
          </div>
        )}
      </div>

      <div className="flex gap-2 flex-wrap mb-6">
        {formData.interests.map((interest) => (
          <span
            key={interest.id}
            className="flex items-center gap-2 border border-black rounded-full pl-4 pr-1.5 py-1.5 text-sm font-bold"
          >
            {interest.label}
            <button
              type="button"
              onClick={() => toggleInterest(interest)}
              className="w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center shrink-0"
            >
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3"><path d="M18 6L6 18M6 6l12 12" /></svg>
            </button>
          </span>
        ))}
      </div>

      <label className="block text-sm font-semibold text-black mb-2">Device &amp; Tech</label>
      <div className="mb-6">
        <PillGroup options={DEVICES} value={formData.devices} onChange={(v) => updateFormData({ devices: v })} />
      </div>

      <div className="flex items-center justify-between mb-4">
        <span className="text-sm font-semibold text-black">Connection to Seller</span>
        <Toggle checked={formData.connectToSeller} onChange={(v) => updateFormData({ connectToSeller: v })} />
      </div>
      <div className="flex items-center justify-between mb-6">
        <span className="text-sm font-semibold text-black">Previous Brand Buyers</span>
        <Toggle checked={formData.previousBrandBuyers} onChange={(v) => updateFormData({ previousBrandBuyers: v })} />
      </div>

      <label className="block text-sm font-semibold text-black mb-2">Select Placement</label>
      <div className="space-y-3 mb-8">
        {PLACEMENTS.map((p) => (
          <label
            key={p.key}
            className="flex items-start gap-3 bg-[#fefdeb] border border-[rgba(195,197,217,0.6)] rounded-2xl p-4 cursor-pointer"
          >
            <div className="mt-0.5" onClick={() => togglePlacement(p.key)}>
              <CheckBox checked={formData.placements[p.key]} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-[#131b2e]">{p.label}</span>
                {p.badge && (
                  <span className="bg-[#eef000] text-[10px] font-bold px-2 py-0.5 rounded-full">{p.badge}</span>
                )}
              </div>
              <p className="text-xs text-[#747474] mt-0.5">{p.description}</p>
            </div>
          </label>
        ))}
      </div>

      <div className="flex gap-3">
        <button
          type="button"
          onClick={onBack}
          className="border border-[#c3c5d9] text-[#434656] font-medium text-lg px-8 py-3 rounded-full"
        >
          Back
        </button>
        <button
          type="button"
          disabled={!canContinue}
          onClick={onNext}
          className="bg-[#095228] border border-[#39b54a] text-[#f9e9ea] font-medium text-lg px-10 py-3 rounded-full shadow-[0_0_6px_rgba(59,182,76,0.53)] disabled:opacity-50"
        >
          Continue
        </button>
      </div>
    </div>
  );
}
